/* eslint-disable jsx-a11y/alt-text */
import './css/RunPage.css';

import { Share } from '@capacitor/share';
import { Filesystem, FilesystemDirectory } from '@capacitor/filesystem';
import axios from 'axios';

import {
  IonButtons,
  IonContent,
  IonPage,
  useIonPopover,
  useIonToast,
  useIonAlert,
  IonProgressBar,
} from '@ionic/react';

import { observer } from 'mobx-react-lite';
import { useCallback, useMemo, useState, useEffect } from 'react';
import { useHistory, useParams } from 'react-router';
import { MESSAGE_ALIVE_MS, MESSAGE_POSITION, SHARING_URL } from 'util/constants';
import { formatScore, getRunScore } from '../../util/freak_ranker';
import { useStores } from '../../models';
import { RunModel } from '../../models/RunModel/RunModel';
import PopUpMenu from './PopUpMenu';
import ViewFigureInfo from './ViewFigureInfo';
import { PreviewTopBar, TopBar } from './TopBars';
import VideoPlayerView from './VideoPlayerView';
import ViewRun from 'components/ViewRun';
import { generateContext } from 'services/utils';
import { useTranslation } from 'react-i18next';
import { logger } from 'services/Logger';
import DynamicButton from 'components/buttons/DynamicButton';
import _ from 'lodash';
import { freakVideoService } from 'services/freak_video';
import { buildOverlayDataFromPlay } from 'services/freak_video';

interface IRunEditContext {
  isEdit: boolean;
  onSectionRename: (
    idx: number,
    name: string,
    angle: number,
    has_backside: boolean,
    stance: string,
    achived: boolean,
    rotation?: string
  ) => void;
  startEditMode: () => void;
}

export const [RunEditContextProvider, useRunEditContext] =
  generateContext<IRunEditContext>();

interface RunPageProps {
  record: RunModel;
  preview?: boolean;
  shared?: boolean;
}

const RunPage: React.FC<RunPageProps> = observer((props: RunPageProps) => {
  const { preview, shared, record } = props;
  const { t } = useTranslation();
  const [present] = useIonToast();
  const [presentDeleteAlert] = useIonAlert();
  const { recordList, currentUser, appData } = useStores();
  const history = useHistory();
  const [overlayActive, setOverlayActive] = useState(false);
  const [videoActive, setVideoActive] = useState(false);
  const [toast, dismissToast] = useIonToast();
  const [editMode, setEditMode] = useState(false);

  // ✅ Verwende die Einstellung aus appData.displaySettings
  const [showUfoScore, setShowUfoScore] = useState(
    appData.displaySettings.defaultViewMode === 'score'
  );

  // G-Force Display State - verwende Settings
  const showGForce = appData.displaySettings.showGForce;

  // ✅ Überwache Änderungen der defaultViewMode Einstellung
  useEffect(() => {
    setShowUfoScore(appData.displaySettings.defaultViewMode === 'score');
  }, [appData.displaySettings.defaultViewMode]);

  const onSectionRename = useCallback(
    (
      idx: number,
      name: string,
      angle: number,
      has_backside: boolean,
      new_stance: string,
      new_achived: boolean,
      new_rotation?: string
    ) => {
      logger.debug(
        `section rename from ${record.playData!.figures![idx].name} to ${name}`,
        logger.debug(
          'changes figure angle from',
          record.playData!.figures![idx].angle,
          'to',
          angle
        ),
        logger.debug(
          'changes figure has_backside from',
          record.playData!.figures![idx].has_backside,
          'to',
          has_backside
        )
      );
      record.renameFigure(
        idx,
        name,
        angle,
        has_backside,
        new_stance,
        new_achived,
        new_rotation
      );
    },
    [record]
  );

  const onEditStart = useCallback(() => {
    logger.debug('startEditMode');
    setEditMode(true);
  }, [setEditMode]);

  const onEditEnd = useCallback(() => {
    logger.debug('endEditMode');
    record.updatePlayData();
    setEditMode(false);
  }, [record, setEditMode]);

  const onEditAbort = () => {
    record.resetFigureNames();
    // trigger record refresh
    record.setPlayData(_.clone(record.playData!)); // this is so hacky.. I'm sorry..
    setEditMode(false);
  };

  const [renderProgress, setRenderProgress] = useState<number | null>(null);

  // This hook creates a popover for the menu.
  const [_showOptions, dismiss] = useIonPopover(PopUpMenu, {
    isFavorite: record?.favorite ?? false,
    hasVideo: !!record?.localVideoUri,
    onStar: starClicked,
    onHide: () => dismiss(),
    onDelete: trashBinClicked,
    onShare: shareClicked,
    onVideoRender: videoRenderClicked,
    renderProgress,
    onInfo: () => {
      setOverlayActive(true);
      dismiss();
    },
    onEditMode: () => {
      onEditStart();
      dismiss();
    },
  });

  if (!record) {
    history.goBack();
    return <></>;
  }

  const playData = record?.playData;

  const goBackToRunList = async (
    ev: React.MouseEvent<HTMLIonButtonElement, MouseEvent>,
    remove: boolean = false
  ) => {
    ev.preventDefault();
    if (history.length > 1) {
      history.goBack();
    } else {
      history.replace('/profile');
    }
    // if ((preview || remove) && record) {
    //   try {
    //     await recordList.deleteRun(record);
    //     await api.deleteRun(record.deviceId, record.recordId);
    //   } catch (_e) {
    //     toast(
    //       makeErrorToast(t('ERROR'), t('COULDNT_DELETE_TRICK'), dismissToast)
    //     );
    //   }
    // }
  };

  function trashBinClicked(
    ev: React.MouseEvent<HTMLIonButtonElement, MouseEvent>
  ) {
    ev.preventDefault();

    // Show confirmation dialog like in Profile page
    presentDeleteAlert({
      header: t('DELETE_RUN_HEADER', 'Run löschen?'),
      message: t(
        'DELETE_RUN_CONFIRM',
        'Möchten Sie diese Run wirklich löschen? Diese Aktion kann nicht rückgängig gemacht werden.'
      ),
      buttons: [
        {
          text: t('CANCEL', 'Abbrechen'),
          role: 'cancel',
        },
        {
          text: t('DELETE', 'Löschen'),
          role: 'destructive',
          handler: async () => {
            try {
              // Execute the actual deletion
              goBackToRunList(ev, true);
              dismiss();

              present({
                message: t(
                  'RUN_DELETED_SUCCESS',
                  'Run erfolgreich gelöscht.'
                ),
                duration: MESSAGE_ALIVE_MS,
                color: 'success',
                position: MESSAGE_POSITION,
              });
            } catch (error) {
              present({
                message: t(
                  'RUN_DELETE_FAILED',
                  'Run konnte nicht gelöscht werden.'
                ),
                duration: MESSAGE_ALIVE_MS,
                color: 'danger',
                position: MESSAGE_POSITION,
              });
            }
          },
        },
      ],
    });
  }

  function starClicked() {
    dismiss();
    if (!record?.shareUrl || !currentUser) return;
    currentUser.toggleFavorite(record.shareUrl).then((ret) => {
      if (ret == null) return; // error thrown.
      record!.setFavorite(ret);
    });
  }

  async function videoRenderClicked() {
    if (!record?.id) {
      present({
        message: 'Kein Video-Name gefunden!',
        duration: MESSAGE_ALIVE_MS,
        color: 'danger',
        position: MESSAGE_POSITION,
      });
      return;
    }
    setRenderProgress(0);
    present({
      message: 'Rendering started. You will be notified when done 🤙',
      duration: 2500,
      color: 'primary',
      position: MESSAGE_POSITION,
    });

    try {
      const overlayData = buildOverlayDataFromPlay(record.playData, 30, 0.1);

      // Dateiname IMMER im Format Video_<id>.mp4
      let fileName = record.id;
      if (!fileName.startsWith('Video_')) fileName = 'Video_' + fileName;
      if (!fileName.endsWith('.mp4')) fileName = fileName + '.mp4';

      // Nur Render-Request, kein Download!
      await freakVideoService.render(fileName, overlayData);

      present({
        message: 'Rendering abgeschlossen! Du kannst das Video jetzt teilen.',
        duration: MESSAGE_ALIVE_MS,
        color: 'success',
        position: MESSAGE_POSITION,
      });
      logger.debug('Render erfolgreich');

      if (!record?.id || !currentUser) return;

      let fileNameShare = record.id;
      if (!fileNameShare.startsWith('RenderedTM_')) fileNameShare = 'RenderedTM_' + fileNameShare.replace(/^Video_/, '');
      if (!fileNameShare.endsWith('.mp4')) fileNameShare = fileNameShare + '.mp4';

      try {
        // UID vom aktuellen User holen
        const uid = currentUser.uid;
        if (!uid) throw new Error('Kein User-UID gefunden!');

        const env = process.env.NODE_ENV === 'production' ? 'live' : 'test';
        const path = `live/v_1/Rendered/${uid}/${fileNameShare}`;
        const localUri =
          await record.environment.cloudStorage.downloadVideo(path);
        await Share.share({ files: [localUri] });
        logger.debug('Share erfolgreich');
      } catch (err) {
        logger.error('Download/Share-Fehler:', err);
        let msg = 'Download/Teilen fehlgeschlagen.';
        if (err && typeof err === 'object' && 'message' in err && typeof err.message === 'string') {
          if (err.message.includes('storage/object-not-found') || err.message.includes('does not exist')) {
            msg = 'Das gerenderte Video existiert noch nicht auf dem Server. Bitte rendere das Video zuerst.';
          }
        }
        present({
          message: msg,
          duration: MESSAGE_ALIVE_MS,
          color: 'danger',
          position: MESSAGE_POSITION,
        });
      }
    } catch (err) {
      logger.error('VideoService-Fehler:', err);
      present({
        message: 'Rendern fehlgeschlagen.',
        duration: MESSAGE_ALIVE_MS,
        color: 'danger',
        position: MESSAGE_POSITION,
      });
    } finally {
      setRenderProgress(null);
    }
    dismiss();
  }

  async function shareClicked() {
    if (!record?.shareUrl) return;

    await Share.share({
      title: t('TRICK_SHARE_TITLE')!!,
      text: playData!.title,
      url: `${SHARING_URL}/${record.shareUrl}`,
      dialogTitle: t('SHARE_WITH_BUDDIES')!!,
    });
    dismiss();
  }

  const toggleVideoView = () => {
    setVideoActive(!videoActive);
  };

  const editContext = useMemo<IRunEditContext>(
    () => ({
      isEdit: editMode,
      onSectionRename,
      startEditMode: onEditStart,
    }),
    [onSectionRename, editMode, onEditStart]
  );

  const saveTrick = async () => {
    if (!record || !currentUser) return;
    let newTrick: RunModel;
    try {
      if (shared) {
        newTrick = recordList.addFriendRun(record);
        await currentUser.addFriendRun(newTrick.shareUrl!);
      } else {
        // Warten auf addNewRun, um sicherzustellen, dass Änderungen vollständig abgeschlossen sind
        newTrick = await recordList.addNewRun(record);
      }
      // Eine kurze Verzögerung, um sicherzustellen, dass die UI-Updates abgeschlossen sind
      setTimeout(() => {
        history.replace(`/profile`);
      }, 100);
    } catch (error) {
      present({
        message: 'Fehler beim Speichern der Figur',
        duration: MESSAGE_ALIVE_MS,
        color: 'danger',
        position: MESSAGE_POSITION,
      });
    }
  };

  return (
    <IonPage>
      <IonContent>
        <div id="trick-view-container">
          {!editMode &&
            (preview || shared ? (
              <PreviewTopBar
                videoActive={videoActive}
                onVideoToggle={toggleVideoView}
                hasVideo={!!record.localVideoUri}
                onViewFigureInfo={() => setOverlayActive(true)}
                onDiscard={goBackToRunList}
                onSave={saveTrick}
                showUfoScore={showUfoScore}
                onToggleUfoScore={() => setShowUfoScore(!showUfoScore)}
              />
            ) : (
              <TopBar
                videoActive={videoActive}
                onVideoToggle={toggleVideoView}
                hasVideo={!!record.localVideoUri}
                onViewFigureInfo={() => setOverlayActive(true)}
                onBack={goBackToRunList}
                onDelete={trashBinClicked}
                showUfoScore={showUfoScore}
                onToggleUfoScore={() => setShowUfoScore(!showUfoScore)}
                onShowMenu={(e: React.MouseEvent<HTMLIonButtonElement>) =>
                  _showOptions({ event: e.nativeEvent })
                }
                onShare={shareClicked}
                onVideoRender={videoRenderClicked}
                isPublic={record.isPublic}
              />
            ))}
          {editMode && (
            <div className="navbar canvas-overlay notchSafe">
              <IonButtons slot="end">
                <DynamicButton
                  text={'Edit end'}
                  fill="clear"
                  iconName="OKUpIcon"
                  buttonType="icon-full"
                  color="primary"
                  size="xlarge"
                  onClick={onEditEnd}
                />
              </IonButtons>
              <IonButtons slot="end">
                <DynamicButton
                  text={'Edit abort'}
                  fill="clear"
                  iconName="CancelUpIcon"
                  buttonType="icon-full"
                  color="primary"
                  size="xlarge"
                  onClick={onEditAbort}
                />
              </IonButtons>
            </div>
          )}

          {/* UFO Score Display - Toggleable */}
          {showUfoScore &&
            (() => {
              const runScore = getRunScore(record);
              const userName = record?.skater || 'Unknown User';
              const isInContest = false;
              const runNumber = 1;
              return (
                <div
                  style={{
                    position: 'absolute',
                    top: editMode ? '150px' : '130px',
                    left: '0px',
                    zIndex: 2,
                    borderRadius: '0 8px 8px 0',
                    backgroundColor: 'transparent',
                    color: 'transparent',
                  }}
                >
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      backgroundColor: 'var(--ion-color-primary)',
                      borderRadius: '0 8px 0 0',
                      padding: '6px 16px',
                      paddingLeft: '12px',
                      height: '36px',
                      minWidth: '140px',
                    }}
                  >
                    <span
                      style={{
                        color: 'white',
                        fontSize: '18px',
                        fontWeight: 'bold',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        flex: 1,
                      }}
                    >
                      {userName}
                    </span>
                    {isInContest && (
                      <span
                        style={{
                          color: 'white',
                          fontSize: '18px',
                          fontWeight: 'bold',
                          marginLeft: '8px',
                        }}
                      >
                        Run {runNumber}
                      </span>
                    )}
                  </div>
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'flex-start',
                      backgroundColor: 'var(--ion-color-secondary)',
                      borderRadius: '0 0 8px 0',
                      padding: '6px 12px',
                      paddingLeft: '12px',
                      height: '36px',
                      width: 'fit-content',
                    }}
                  >
                    <span
                      style={{
                        color: 'white',
                        fontSize: '18px',
                        fontWeight: 'bold',
                      }}
                    >
                      Score: {formatScore(runScore)}
                    </span>
                  </div>
                </div>
              );
            })()}

          <RunEditContextProvider value={editContext}>
            <div style={{ width: '100%', height: '100%' }}>
              <VideoPlayerView
                videoActive={videoActive}
                onVideoToggle={toggleVideoView}
                hasVideo={!!record.localVideoUri}
                record={record}
                hidden={!videoActive}
                showRunStatsBox={!showUfoScore}
                showFigurePopup={showUfoScore}
                showGForceDisplay={showGForce && !showUfoScore}
              />
              <ViewRun
                record={record}
                hidden={videoActive}
                videoActive={videoActive}
                onVideoToggle={toggleVideoView}
                hasVideo={!!record.localVideoUri}
                showRunStatsBox={!showUfoScore}
                showFigurePopup={showUfoScore}
                showGForceDisplay={showGForce && !showUfoScore}
              />
            </div>
          </RunEditContextProvider>

          {overlayActive && (
            <div
              style={{
                position: 'absolute',
                inset: 0,
                zIndex: 1001,
                background: 'rgba(0,0,0,0.15)',
              }}
            >
              <ViewFigureInfo
                dismiss={() => setOverlayActive(false)}
                record={record}
                isPublic={record.isPublic}
              />
            </div>
          )}
        </div>
      </IonContent>
    </IonPage>
  );
});

export const RunPreview = () => {
  const { recordList } = useStores();
  const params = useParams<{ id: string }>();
  const record = recordList.tempRuns.find((trick) => trick.id === params.id);

  if (!record) return <></>;

  return <RunPage preview record={record} />;
};

export default RunPage;
